import { CartoContext } from "./carto-context"
import * as React from "react";
import L from 'leaflet'
import carto from '@carto/carto.js'
import { connect } from 'react-redux';

import { MAP_CONFIG } from './../MAP_CONFIG';
import { initCarto, loadMap, loadLayer, loadDataview, updateLayerFilter, resetLayers, addToMap, addPopup, clearFilters  } from '../actions/actions';

require('leaflet-easybutton');

class CartoProvider extends React.Component {
  carto
  map
  constructor(props) {
    super(props)
    this.state = {
      layers: {},
      dataviews: {},
      initCarto: this.initCarto,
      loadMap: this.loadMap,
      loadLayers: this.loadLayers,
      loadLayer: this.loadLayer,
      loadDataview: this.loadDataview,
      resetLayers: this.resetLayers,
      addToMap: this.addToMap,
      toggleLayer: this.toggleLayer,
      updateLayerFilter: this.updateLayerFilter,
      clearFilters: this.clearFilters,
      addFeatureLayer: this.addFeatureLayer,
      addPopup: this.addPopup,
    }
  }

  initCarto = () => {
    const config = {
      username: process.env.REACT_APP_CARTO_USERNAME,
      apiKey: process.env.REACT_APP_CARTO_APIKEY
    }
    this.carto = new carto.Client(config);
    this.props.initCarto()
  }
  
  loadMap = (config) => {
    if (this.map) this.map.remove()
    this.map = L.map('map', {maxZoom: MAP_CONFIG.maxZoom, zoomControl: false})
    this.map.on('load', this.handleMapLoad)
    this.map.setView([MAP_CONFIG.center[1], MAP_CONFIG.center[0]], MAP_CONFIG.zoom);
    
    L.tileLayer(
      MAP_CONFIG.style,
      {
        maxZoom: MAP_CONFIG.maxZoom,
        id: MAP_CONFIG.id,
        accessToken: process.env.REACT_APP_MAPBOX_TOKEN
      }
    ).addTo(this.map);

    L.control.zoom({ position: 'bottomright' }).addTo(this.map);
    
    L.easyButton('<i class="as-icon as-icon-home"></i>', function(btn, map){
      map.setView([MAP_CONFIG.center[1], MAP_CONFIG.center[0]], MAP_CONFIG.zoom);
    }, {
      position: 'bottomright',
    }).addTo( this.map );

    this.props.loadMap()
  }

  // suspect that airship responsive content calls ready prematurely and the map needs resized on load
  handleMapLoad = () => {
    setTimeout(() => {
      this.map.invalidateSize();
    }, 500)
    this.map.off('load', this.handleMapLoad)
  }
  
  loadLayer = (layer) => {
    const cartoLayer = new carto.layer.Layer(
      new carto.source.SQL(layer.sql),
      new carto.style.CartoCSS(layer.cartoCSS),
      {featureOverColumns: layer.featureOverColumns}
    )
    if (layer.filters) {
      let cartoFilters = Object.keys(layer.filters).map(field => new carto.filter.Category(field, { in: layer.filters[field] })) 
      cartoLayer.getSource().addFilters(cartoFilters)
    }
    
    let promise = this.carto.addLayer(cartoLayer)
    if (layer.hide) cartoLayer.hide()
    if (layer.index) cartoLayer.setOrder(layer.index)
    if (layer.callback) promise.then(() => {
      if (layer.popup) {
        this.addPopup(layer.name, layer.popup.eventName, layer.popup.popupContent)
      }
      layer.callback('loaded')
    })
    this.setState(state => {
      state.layers[layer.name] = cartoLayer
      return state
    })
    this.props.loadLayer(layer)
  }

  loadLayers = (layers) => {
    layers.forEach(layer => {
      this.loadLayer(layer)
    })
  }

  loadDataview = (dataview) => {
    const cartoDataview = new carto.dataview[dataview.type](new carto.source.SQL(dataview.source), dataview.field, {...dataview.options, operation: carto.operation[dataview.options.operation]})
    const bboxFilter = new carto.filter.BoundingBoxLeaflet(this.map)
    cartoDataview.addFilter(bboxFilter)
    this.carto.addDataview(cartoDataview)

    this.setState(state => {
      state.dataviews[dataview.name] = cartoDataview
      return state
    })
    this.props.loadDataview(dataview)
  }
  
  resetLayers = () => {
    this.setState(state => ({
      ...state,
      layers: {}
    }))
    
    this.props.resetLayers()
  }
  
  addToMap = () => {
    this.carto.getLeafletLayer().addTo(this.map)
  }
  
  toggleLayer = (layerName, show) => {
    let layer = this.state.layers[layerName]
    if (show) layer.show()
    else layer.hide()
    this.props.toggleLayer(layerName, show)
  }
  
  updateLayerFilter = (layerName, field, values, label) => {
    this.props.updateLayerFilter(layerName, field, values, label)
    const layer = this.props.layers[layerName] || this.props.dataviews[layerName]
    const cartoLayer = this.state.layers[layerName]
    const cartoDataview = this.state.dataviews[layerName]
    if (cartoLayer) {
      if (layer.callback) layer.callback('loading')
      this.carto.removeLayer(cartoLayer)
      this.loadLayer(layer)
    }
    if (cartoDataview) {
      const source = cartoDataview.getSource()
      const filters = source.getFilters()
      while (filters.length > 0) {
        source.removeFilter(filters[0])
      }
      let newFilters = Object.keys(layer.filters).map(field => new carto.filter.Category(field, { in: layer.filters[field] })) 
      source.addFilters(newFilters)
    }
  }
  
  clearFilters = (layerName) => {
    const layer = this.props.layers[layerName] || this.props.dataviews[layerName]
    if (!Object.keys(layer.filters).length) return
    const cartoLayer = this.state.layers[layerName]
    const cartoDataview = this.state.dataviews[layerName]
    this.props.clearFilters(layerName).then(() => {
      if (cartoLayer) {
        if (layer.callback) layer.callback('loading')
        this.carto.removeLayer(cartoLayer)
        this.loadLayer(this.props.layers[layerName])
      }
      if (cartoDataview) {
        const source = cartoDataview.getSource()
        const filters = source.getFilters()
        while (filters.length > 0) {
          source.removeFilter(filters[0])
        }
      }
    })
  }

  addPopup = (layerName, eventName, popupContent) => {
    if (!this.props.layers[layerName]) return
    this.props.addPopup(layerName, eventName, popupContent)
    let layer = this.state.layers[layerName]
    layer.off(eventName)
    const popup = L.popup({
      closeButton: false,
      closeOnClick: true
    });
    layer.on(eventName, (featureEvent) => {
      this.updatePopup(popup, featureEvent, popupContent)
    });
  }
  
  updatePopup = (popup, featureEvent, popupContent) => {
    const content = popupContent(featureEvent.data)
    popup.setContent(content);
    popup.setLatLng(featureEvent.latLng);
    if (!popup.isOpen()) {
      popup.openOn(this.map);
    }
  }

  render() {
    return (
      <CartoContext.Provider
        value={{
          CartoContext: {
            ...this.state
          }
        }}
      >
        {this.props.children}
      </CartoContext.Provider>
    )
  }
}

const mapStateToProps = state => ({
  layers: state.carto.layers,
  dataviews: state.carto.dataviews,
});

export default connect(mapStateToProps, { initCarto, loadMap, loadLayer, loadDataview, updateLayerFilter, resetLayers, addToMap, addPopup, clearFilters })(CartoProvider);
